import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { api } from '../api';
import { useAuth } from './AuthContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

interface DataSyncContextValue {
  adminPendingCount: number;
  myPendingCount: number;
  openReportCount: number;
  refreshAdminPending: () => Promise<void>;
  refreshMyPending: () => Promise<void>;
  refreshOpenReportCount: () => Promise<void>;
  refreshAllBadges: () => Promise<void>;
  triggerRouteRefresh: (path: string) => void;
  routeRefreshCounters: Record<string, number>;
}

const DataSyncContext = createContext<DataSyncContextValue | undefined>(undefined);

export function DataSyncProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [adminPendingCount, setAdminPendingCount] = useState(0);
  const [myPendingCount, setMyPendingCount] = useState(0);
  const [openReportCount, setOpenReportCount] = useState(0);
  const [routeRefreshCounters, setRouteRefreshCounters] = useState<Record<string, number>>({});
  const refreshInFlight = useRef(false);
  const badgeRefreshTick = useAutoRefresh(5000, { enabled: Boolean(user) });

  const refreshAdminPending = useCallback(async () => {
    if (!user || user.role !== 'admin') {
      setAdminPendingCount(0);
      return;
    }

    try {
      const count = await api.flowCount({ status: 'pending' });
      setAdminPendingCount(count);
    } catch (error) {
      console.error('Unable to fetch admin pending count', error);
    }
  }, [user]);

  const refreshMyPending = useCallback(async () => {
    if (!user) {
      setMyPendingCount(0);
      return;
    }

    try {
      const count = await api.flowCount({ ownerId: user.id, status: 'pending' });
      setMyPendingCount(count);
    } catch (error) {
      console.error('Unable to fetch my pending count', error);
    }
  }, [user]);

  const refreshOpenReportCount = useCallback(async () => {
    if (!user || user.role !== 'admin') {
      setOpenReportCount(0);
      return;
    }
    try {
      const data = await api.openReportCount();
      setOpenReportCount(data.count);
    } catch (error) {
      console.error('Unable to fetch open report count', error);
    }
  }, [user]);

  const refreshAllBadges = useCallback(async () => {
    await Promise.all([refreshAdminPending(), refreshMyPending(), refreshOpenReportCount()]);
  }, [refreshAdminPending, refreshMyPending, refreshOpenReportCount]);

  useEffect(() => {
    void refreshAllBadges();
  }, [refreshAllBadges]);

  useEffect(() => {
    if (!user || refreshInFlight.current) {
      return;
    }

    refreshInFlight.current = true;
    void refreshAllBadges().finally(() => {
      refreshInFlight.current = false;
    });
  }, [badgeRefreshTick, refreshAllBadges, user]);

  useEffect(() => {
    if (!user) {
      setRouteRefreshCounters({});
    }
  }, [user]);

  const triggerRouteRefresh = useCallback((path: string) => {
    setRouteRefreshCounters((current) => ({
      ...current,
      [path]: (current[path] ?? 0) + 1
    }));
  }, []);

  const value = useMemo(
    () => ({
      adminPendingCount,
      myPendingCount,
      openReportCount,
      refreshAdminPending,
      refreshMyPending,
      refreshOpenReportCount,
      refreshAllBadges,
      triggerRouteRefresh,
      routeRefreshCounters
    }),
    [
      adminPendingCount,
      myPendingCount,
      openReportCount,
      refreshAdminPending,
      refreshMyPending,
      refreshOpenReportCount,
      refreshAllBadges,
      triggerRouteRefresh,
      routeRefreshCounters
    ]
  );

  return <DataSyncContext.Provider value={value}>{children}</DataSyncContext.Provider>;
}

export function useDataSync() {
  const context = useContext(DataSyncContext);
  if (!context) {
    throw new Error('useDataSync must be used within a DataSyncProvider');
  }
  return context;
}

export function useRouteRefresh(path: string) {
  const { routeRefreshCounters } = useDataSync();
  return routeRefreshCounters[path] ?? 0;
}
